import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { useAuth } from '../App';
import { API } from '../utils';
import { toast } from 'sonner';
import { CheckCircle, Bug, Lightbulb, ThumbsUp, HelpCircle, Send } from 'lucide-react';

const ghostBtn = "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[#e4e1db] text-[#5a5a5a] text-[12px] font-medium hover:border-[#0e0e0e] hover:text-[#0e0e0e] transition-colors cursor-pointer";
const inkBtn   = "inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0e0e0e] text-white text-[12px] font-medium hover:bg-[#222] transition-colors disabled:opacity-50 cursor-pointer";

const Feedback = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const config = { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };

  const categories = [
    { value: 'bug',         label: 'Report a Bug',     icon: Bug,         description: "Something isn't working right" },
    { value: 'feature',     label: 'Feature Request',  icon: Lightbulb,   description: 'Suggest a new feature' },
    { value: 'improvement', label: 'Improvement',      icon: ThumbsUp,    description: 'Make something better' },
    { value: 'other',       label: 'Other',            icon: HelpCircle,  description: 'General feedback' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) { toast.error('Please select a category'); return; }
    if (!message.trim()) { toast.error('Please enter your feedback'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, { category, message: message.trim() }, config);
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Success state ── */
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f7f5f2] flex items-center justify-center p-6">
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-10 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-[#f0fdf4] border border-[#bbf7d0] rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-7 h-7 text-[#16a34a]" />
          </div>
          <h1 className="font-display font-normal text-[26px] text-[#0e0e0e] tracking-[-0.4px] mb-2">Thank You!</h1>
          <p className="text-[13px] text-[#a0a0a0] mb-7 leading-relaxed">
            Your feedback has been submitted. We truly appreciate you taking the time to help us improve FrameFinder.
          </p>
          <div className="flex gap-2.5 justify-center">
            <button onClick={() => navigate(-1)} className={ghostBtn}>Go Back</button>
            <button
              onClick={() => { setSubmitted(false); setMessage(''); setCategory(''); }}
              className={inkBtn}
            >
              Submit More Feedback
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f5f2]">

      {/* ── Page Header ── */}
      <div className="bg-white border-b border-[#e4e1db] px-5 md:px-10 pt-7 pb-5">
        <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-1.5">Share</p>
        <h1 className="font-display font-normal text-[28px] md:text-[32px] text-[#0e0e0e] tracking-[-0.5px] leading-[1.1]">
          Give Feedback
        </h1>
      </div>

      {/* ── Content ── */}
      <div className="px-5 md:px-10 py-6 max-w-full space-y-5 pb-20">

        {/* Intro card */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">We're Listening</h2>
          <p className="text-[13px] text-[#a0a0a0] leading-relaxed">
            We're constantly working to make FrameFinder better for photographers, videographers, and businesses.
            Your feedback directly shapes how we improve the platform. Whether it's a bug you've found,
            a feature you'd love to see, or just general thoughts — we want to hear from you.
          </p>
        </div>

        {/* Feedback form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7 space-y-6">
            <div>
              <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Share Your Thoughts</h2>
              <p className="text-[13px] text-[#a0a0a0]">Select a category and describe what's on your mind.</p>
            </div>

            {/* Category cards */}
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-3 block">
                What type of feedback do you have?
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const selected = category === cat.value;
                  return (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setCategory(cat.value)}
                      className={`p-4 rounded-[12px] border text-left transition-all ${
                        selected
                          ? 'border-[#0e0e0e] bg-[#0e0e0e]'
                          : 'border-[#e4e1db] bg-white hover:border-[#0e0e0e]'
                      }`}
                    >
                      <Icon className={`w-4 h-4 mb-2.5 ${selected ? 'text-white' : 'text-[#a0a0a0]'}`} />
                      <p className={`text-[12px] font-semibold ${selected ? 'text-white' : 'text-[#0e0e0e]'}`}>
                        {cat.label}
                      </p>
                      <p className={`text-[11px] mt-0.5 hidden sm:block ${selected ? 'text-white/60' : 'text-[#c0bdb8]'}`}>
                        {cat.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Your Feedback</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what's on your mind... Be as detailed as you'd like!"
                rows={6}
                className="w-full text-[14px] text-[#0e0e0e] border-[#e4e1db] placeholder:text-[#c0bdb8] focus-visible:ring-0 focus-visible:border-[#0e0e0e] focus-visible:shadow-[0_0_0_3px_rgba(14,14,14,0.06)] resize-none"
                data-testid="feedback-message"
              />
              <p className="text-[11px] text-[#c0bdb8] mt-1.5">{message.length} characters</p>
            </div>

            {/* User info */}
            {user && (
              <div className="bg-[#f7f5f2] border border-[#e4e1db] rounded-[10px] px-4 py-3">
                <p className="text-[11px] text-[#a0a0a0] mb-0.5">Submitting as</p>
                <p className="text-[13px] font-semibold text-[#0e0e0e]">{user.name}</p>
                <p className="text-[12px] text-[#a0a0a0]">{user.email}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting || !category || !message.trim()}
              className={`${inkBtn} w-full justify-center py-2.5 text-[13px]`}
              data-testid="submit-feedback-btn"
            >
              <Send className="w-3.5 h-3.5" />
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>

        {/* Help link */}
        <p className="text-center text-[12px] text-[#a0a0a0]">
          Need immediate help?{' '}
          <Link to="/help" className="text-[#0e0e0e] font-medium hover:underline">
            Visit our Help Center
          </Link>
        </p>

      </div>
    </div>
  );
};

export default Feedback;
